import { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Bell, Command, Menu, Radio, Search, X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Sidebar, navigationItems, utilityNavigationItems } from '@/components/navigation/sidebar';
import { ToastViewport } from '@/components/ui/toast';
import { CommandMenu } from '@/components/ui/command-menu';
import { ConfirmationViewport } from '@/components/ui/confirmation';
import { api } from '@/services/api';

const titles = Object.fromEntries([...navigationItems, ...utilityNavigationItems].map((item) => [item.path, item.label]));

export function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('convoy:sidebar-collapsed') === '1');
  const title = titles[location.pathname] ?? 'Command';
  const commandItems = useMemo(() => [...navigationItems, ...utilityNavigationItems], []);
  const notificationSummary = useQuery({
    queryKey: ['sidebar-notification-summary'],
    queryFn: () => api('/api/v1/admin/notifications?limit=1').then((result) => result.summary ?? {}),
    refetchInterval: 15_000,
  });
  const unread = Number(notificationSummary.data?.unread ?? 0);

  useEffect(() => {
    localStorage.setItem('convoy:sidebar-collapsed', sidebarCollapsed ? '1' : '0');
  }, [sidebarCollapsed]);

  useEffect(() => {
    document.title = `${title} · Convoy`;
  }, [title]);

  return <div className="h-dvh overflow-hidden bg-[#03070c]">
    <Sidebar
      open={sidebarOpen}
      collapsed={sidebarCollapsed}
      onClose={() => setSidebarOpen(false)}
      onToggleCollapsed={() => setSidebarCollapsed((value) => !value)}
    />
    {sidebarOpen && <button aria-label="Close navigation" className="fixed inset-0 z-30 bg-black/80 backdrop-blur-sm lg:hidden" onClick={() => setSidebarOpen(false)}/>} 
    <div className={`flex h-dvh min-w-0 flex-col transition-[padding] duration-200 ${sidebarCollapsed ? 'lg:pl-[72px]' : 'lg:pl-[232px]'}`}>
      <header className="command-topbar relative z-30 flex h-[52px] shrink-0 items-center justify-between border-b border-cyan-300/10 bg-[#050a11]/95 px-3 backdrop-blur-2xl sm:px-4">
        <div className="flex min-w-0 items-center gap-2.5">
          <button aria-label={sidebarOpen ? 'Close menu' : 'Open menu'} className="grid size-8 place-items-center border border-white/10 text-slate-400 lg:hidden" onClick={() => setSidebarOpen((value) => !value)}>{sidebarOpen ? <X size={17}/> : <Menu size={17}/>}</button>
          <div className="min-w-0 border-l border-cyan-300/20 pl-3">
            <p className="text-[6px] font-black uppercase tracking-[.25em] text-cyan-300">Convoy command center</p>
            <h1 className="truncate text-xs font-black uppercase tracking-[.08em] text-slate-100">Realtime operations</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: true }))} className="hidden h-8 items-center gap-2 border border-white/9 bg-black/20 px-3 text-[9px] text-slate-500 transition hover:border-cyan-300/30 hover:text-cyan-100 sm:flex">
            <Search size={13}/><span>Search command</span><span className="ml-2 border border-white/10 px-1.5 py-0.5 text-[8px]"><Command size={9} className="inline"/> K</span>
          </button>
          <button aria-label="Open notifications" onClick={() => navigate('/notifications')} className="relative grid size-8 place-items-center border border-white/9 bg-black/20 text-slate-500 transition hover:border-cyan-300/30 hover:text-cyan-200"><Bell size={14}/>{unread > 0 && <span className="absolute -right-1 -top-1 min-w-4 bg-cyan-300 px-1 py-0.5 text-[7px] font-black text-[#031015]">{Math.min(99, unread)}</span>}</button>
          <div className="hidden h-8 items-center gap-2 border border-emerald-300/20 bg-emerald-300/[.06] px-3 sm:flex"><Radio size={10} className="text-emerald-300"/><span className="text-[7px] font-black uppercase tracking-[.16em] text-emerald-300">Live</span></div>
        </div>
      </header>
      <main className="min-h-0 flex-1 overflow-hidden">
        <div className="dashboard-workspace"><Outlet/></div>
      </main>
    </div>
    <CommandMenu items={commandItems}/>
    <ConfirmationViewport/>
    <ToastViewport/>
  </div>;
}
